import React, { useState, useMemo, useEffect } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  ChartLegend,
  ChartLegendContent,
  RechartsTooltip as Tooltip,
} from "@capacit-com/lara-law-shared-ui";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@capacit-com/lara-law-shared-ui";
import { ChartContainer } from "@capacit-com/lara-law-shared-ui";
import {
  MostUsedDocument,
  MostUsedDocumentsSortDirection,
  DocumentSourceTypes,
} from "@/models/statistic/Statistic";
import { Input } from "@capacit-com/lara-law-shared-ui";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import useStatistics from "@/hooks/useStatistics";
import { useOpenAppNavigation } from "@/hooks/useOpenAppNavigation";
import {
  legislationTarget,
  workspaceDocumentTarget,
} from "@/lib/navigation/appNavigation";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const docData = payload[0].payload;
    return (
      <div className="bg-background p-2 border border-border rounded-md shadow-md">
        <p className="font-bold">{docData.documentTitle}</p>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {payload.map((entry: any) => (
          <p key={entry.name} style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
        <p className="text-muted-foreground text-xs mt-1">Klik for at åbne</p>
      </div>
    );
  }
  return null;
};

const MostUsedDocGraph: React.FC<{ type: DocumentSourceTypes }> = ({
  type,
}) => {
  const { openNavigation } = useOpenAppNavigation();
  const { useGetMostUsedDocuments } = useStatistics();
  const [mostUsedDocuments, setMostUsedDocuments] = useState<
    MostUsedDocument[]
  >([]);
  const [documentCount, setDocumentCount] = useState(5);
  const [maxChars, setMaxChars] = useState(10);

  const processedDocuments = useMemo(() => {
    if (!mostUsedDocuments || mostUsedDocuments.length === 0) return [];

    const sortedDocuments = [...mostUsedDocuments].sort(
      (a, b) => b.usageCount - a.usageCount
    );

    return sortedDocuments.slice(0, documentCount).map((item) => ({
      ...item,
      documentInitials: item.documentTitle,
    }));
  }, [mostUsedDocuments, documentCount]);

  const fetchedMostUsedDocuments = useGetMostUsedDocuments(
    documentCount,
    MostUsedDocumentsSortDirection.MostUsed,
    type
  );
  const handleDocumentCountChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = Number(e.target.value);
    if (value < 1) {
      setDocumentCount(1);
    } else if (value > 20) {
      setDocumentCount(20);
    } else {
      setDocumentCount(value);
    }
  };

  const isMostUsedDocument = (value: unknown): value is MostUsedDocument => {
    if (typeof value !== "object" || value === null) {
      return false;
    }

    const record = value as Record<string, unknown>;
    return (
      typeof record.documentId === "string" &&
      typeof record.documentTitle === "string" &&
      typeof record.documentType === "string" &&
      typeof record.documentSource === "string" &&
      typeof record.usageCount === "number"
    );
  };

  const handleBarClick = (
    data: unknown,
    _index: number,
    event: React.MouseEvent
  ) => {
    if (typeof data !== "object" || data === null) {
      return;
    }

    const payload =
      "payload" in data ? (data as { payload?: unknown }).payload : undefined;

    if (!isMostUsedDocument(payload)) {
      return;
    }

    if (type === DocumentSourceTypes.Workspace) {
      if (payload.workspaceId && payload.documentId) {
        openNavigation(
          workspaceDocumentTarget(payload.workspaceId, payload.documentId),
          event
        );
      }
    } else if (payload.documentId && payload.documentSource) {
      openNavigation(
        legislationTarget(payload.documentId, payload.documentSource),
        event
      );
    }
  };

  useEffect(() => {
    if (fetchedMostUsedDocuments.isFetched && fetchedMostUsedDocuments.data) {
      setMostUsedDocuments(fetchedMostUsedDocuments.data);
    }
  }, [fetchedMostUsedDocuments.data, fetchedMostUsedDocuments.isFetched]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        const charWidth = 8; // Approximate width of each character
        const maxChars = Math.floor(
          width / processedDocuments.length / charWidth
        );
        setMaxChars(maxChars);
      }
    });

    const chartContainer = document.querySelector(".recharts-wrapper");
    if (chartContainer) {
      resizeObserver.observe(chartContainer);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [processedDocuments.length]);
  return (
    <Card className="rounded-lg shadow-md">
      <CardHeader className="flex items-center gap-2 space-y-0 py-5 sm:flex-row">
        <div className="grid flex-1 gap-1 text-center sm:text-left">
          <CardTitle>
            Dokumentoversigt -{" "}
            {type === DocumentSourceTypes.Workspace
              ? "Workspace dokumenter"
              : "Kilder"}
          </CardTitle>
          <CardDescription className="text-xs">
            Overblik over de mest sete{" "}
            {type === DocumentSourceTypes.Workspace
              ? "workspace dokumenter"
              : "kilder"}
            . Dette er ikke begrænset til den valgte periode.
          </CardDescription>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label htmlFor="documentCount" className="text-sm">
              Antal dokumenter:
            </label>
            <Input
              id="documentCount"
              type="number"
              min={1}
              max={20}
              value={documentCount}
              onChange={handleDocumentCountChange}
              className="w-20"
            />
          </div>
        </div>
      </CardHeader>
      {mostUsedDocuments && mostUsedDocuments.length > 0 ? (
        <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
          <ChartContainer
            config={{
              usageCount: {
                label: "Antal gange set",
                color:
                  type === DocumentSourceTypes.Workspace
                    ? "var(--chart-7)"
                    : "var(--chart-6)",
              },
            }}
            className="aspect-auto h-[300px] w-full"
          >
            <BarChart
              accessibilityLayer
              data={processedDocuments}
              layout="horizontal"
            >
              <defs>
                {type === DocumentSourceTypes.Workspace ? (
                  <linearGradient
                    id="barGradientDocument"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor="var(--chart-7)"
                      stopOpacity={1}
                    />
                    <stop
                      offset="95%"
                      stopColor="var(--chart-7)"
                      stopOpacity={0.5}
                    />
                  </linearGradient>
                ) : (
                  <linearGradient
                    id="barGradientLegislation"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor="var(--chart-6)"
                      stopOpacity={1}
                    />
                    <stop
                      offset="95%"
                      stopColor="var(--chart-6)"
                      stopOpacity={0.5}
                    />
                  </linearGradient>
                )}
              </defs>

              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="documentTitle"
                tickFormatter={(value) => {
                  if (value.length > maxChars) {
                    return value.slice(0, maxChars - 3) + "...";
                  }
                  return value;
                }}
              />
              <YAxis type="number" />
              <Tooltip content={<CustomTooltip />} />
              <ChartLegend
                content={<ChartLegendContent />}
                wrapperStyle={{ top: -30, right: 0 }}
              />
              <Bar
                dataKey="usageCount"
                fill={
                  type === DocumentSourceTypes.Workspace
                    ? "url(#barGradientDocument)"
                    : "url(#barGradientLegislation)"
                }
                radius={4}
                name="Antal gange set"
                onClick={handleBarClick}
                style={{ cursor: "pointer" }}
              />
            </BarChart>
          </ChartContainer>
        </CardContent>
      ) : fetchedMostUsedDocuments.isPending ? (
        <CardContent className="px-auto mx-auto flex justify-center items-center h-full py-7">
          <Spinner className="size-6" />
        </CardContent>
      ) : (
        <CardContent className="px-auto mx-auto flex justify-center items-center h-full py-10">
          <p>Ingen data</p>
        </CardContent>
      )}
    </Card>
  );
};

export default MostUsedDocGraph;
